import { apiGet } from './utils';

export interface PeriodParams {
  [key: string]: string | undefined;
  period?: 'day' | 'week' | 'month';
  start?: string;
  end?: string;
}

export interface MetricsOverview {
  mentor_queue_depth: number;
  mentor_in_review: number;
  period_stats: {
    tasks_claimed: number;
    responses_submitted: number;
    mentor_approvals: number;
    mentor_denials: number;
    tasks_solved: number;
  };
}

export interface PhaseStats {
  label: string;
  count: number;
  avg: number | null;
  median: number | null;
  p95: number | null;
}

export interface MetricsLifecycle {
  phases: Record<string, PhaseStats>;
}

export interface DenialReason {
  reason: string;
  count: number;
}

export interface MetricsQuality {
  total_decisions: number;
  approvals: number;
  denials: number;
  approval_rate: number | null;
  escalations: number;
  denial_reasons: DenialReason[];
}

export interface AssistantMetric {
  key: string;
  name: string;
  approval_rate: number | null;
  approvals: number;
  denials: number;
  avg_response_time: number | null;
  tasks_completed: number;
  claims: number;
}

export interface MentorMetric {
  key: string;
  name: string;
  approval_rate: number | null;
  approvals: number;
  denials: number;
  total_reviews: number;
  avg_review_time: number | null;
  reviews_per_hour: number | null;
}

export interface DailyThroughput {
  date: string;
  solved: number;
  claimed: number;
  submitted: number;
}

export interface MetricsThroughput {
  daily: DailyThroughput[];
}

export const metricsApi = {
  async getOverview(params: PeriodParams): Promise<MetricsOverview> {
    return apiGet<MetricsOverview>('/metrics/overview', params);
  },

  async getLifecycle(params: PeriodParams): Promise<MetricsLifecycle> {
    return apiGet<MetricsLifecycle>('/metrics/lifecycle', params);
  },

  async getQuality(params: PeriodParams): Promise<MetricsQuality> {
    return apiGet<MetricsQuality>('/metrics/quality', params);
  },

  async getAssistants(params: PeriodParams): Promise<{ assistants: AssistantMetric[] }> {
    return apiGet<{ assistants: AssistantMetric[] }>('/metrics/assistants', params);
  },

  async getMentors(params: PeriodParams): Promise<{ mentors: MentorMetric[] }> {
    return apiGet<{ mentors: MentorMetric[] }>('/metrics/mentors', params);
  },

  async getThroughput(params: PeriodParams): Promise<MetricsThroughput> {
    return apiGet<MetricsThroughput>('/metrics/throughput', params);
  },

  async getMyStats(): Promise<MyStats> {
    return apiGet<MyStats>('/metrics/me');
  },
};

/** A rating average is null until it's based on `min_ratings_to_show` ratings. */
export interface RatingSummary {
  average: number | null;
  count: number;
}

/** The caller's own aggregate stats (GET /metrics/me). Never individual ratings. */
export interface MyStats {
  /** Ratings are only counted up to this date (start of the week, UTC). */
  ratings_as_of: string;
  min_ratings_to_show: number;
  window_days: number;
  rating_30d: RatingSummary;
  rating_all_time: RatingSummary;
  tasks_closed_30d: number;
  mentor_30d: { approvals: number; denials: number };
}
